import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  FormikErrors,
  FormikHandlers,
  FormikHelpers,
  FormikTouched,
} from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { GalleriesEnum_class } from '../../../../common/enums/galleries';
import { ExhibitDto } from '../../exhibits.dtos';

interface IProps {
  values: ExhibitDto;
  errors: FormikErrors<ExhibitDto>;
  touched: FormikTouched<ExhibitDto>;
  handleChange: FormikHandlers['handleChange'];
  handleBlur: FormikHandlers['handleBlur'];
  setFieldValue: FormikHelpers<ExhibitDto>['setFieldValue'];
  setErrors: FormikHelpers<ExhibitDto>['setErrors'];
}

const ExhibitInfo: React.FC<IProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setErrors,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (values.galleryId > 0) handleSetShow(values.galleryId);
  }, [values.galleryId]);

  const handleSetShow = (galleryId: number) => {
    if (
      galleryId === GalleriesEnum_class.enum.Community ||
      galleryId === GalleriesEnum_class.enum.VehicleCity ||
      galleryId === GalleriesEnum_class.enum.TravelingExhibit ||
      galleryId === GalleriesEnum_class.enum.NewAquisitions
    ) {
      setShow(true);
      setFieldValue('dateRequired', true);
      setErrors({});
    } else {
      setShow(false);
      setFieldValue('dateRequired', false);
      setErrors({});
      setFieldValue('startDate', '');
      setFieldValue('endDate', '');
    }
  };

  return (
    <Card>
      <CardContent>
        <Grid item xs={12}>
          <Typography variant='subtitle2'>Exhibit Info</Typography>
        </Grid>
        <FormControl fullWidth required>
          <InputLabel>Gallery</InputLabel>
          <Select
            name='galleryId'
            value={values.galleryId}
            onChange={(e: any) => {
              handleChange(e);
              handleSetShow(e.target.value);
            }}
            onBlur={handleBlur}
            error={Boolean(errors.galleryId)}
            fullWidth
            required
          >
            <MenuItem value={0} disabled>
              Select Gallery
            </MenuItem>
            {Object.keys(GalleriesEnum_class.enumByNumber)
              .filter((f) => isNaN(+f) === false)
              .map((o, i) => (
                <MenuItem key={o.toString() + i} value={+o}>
                  {GalleriesEnum_class.enumByNumber[+o]
                    .match(/[A-Za-z]+/g)
                    ?.map((m) => `${m} `)}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText error>{errors.galleryId}</FormHelperText>
        </FormControl>

        <TextField
          size='small'
          margin='none'
          value={values.name || ''}
          label='Name'
          name='name'
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
          }}
          error={Boolean(errors.name && touched.name)}
          helperText={Boolean(errors.name) && touched.name && errors.name}
          fullWidth
          required
        />
        <TextField
          size='small'
          margin='none'
          value={values.description || ''}
          label='Description'
          name='description'
          onChange={handleChange}
          onBlur={(e) => {
            handleBlur(e);
          }}
          error={Boolean(errors.description && touched.description)}
          helperText={
            Boolean(errors.description) &&
            touched.description &&
            errors.description
          }
          fullWidth
          required
        />
        {show ? (
          <>
            <TextField
              name='startDate'
              label='Start Date'
              value={
                values.startDate === undefined
                  ? new Date()
                  : moment(values.startDate)
                      .add('hours', 8)
                      .format('yyyy-MM-DD')
              }
              type='date'
              fullWidth
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
              }}
              error={Boolean(errors.startDate)}
              helperText={errors.startDate}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
            <TextField
              id='endDate'
              name='endDate'
              label='End Date'
              value={
                values.endDate === undefined
                  ? new Date()
                  : moment(values.endDate).add('hours', 8).format('yyyy-MM-DD')
              }
              type='date'
              fullWidth
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
              }}
              error={Boolean(errors.endDate)}
              helperText={errors.endDate}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
          </>
        ) : null}

        <Grid container justify='flex-end'>
          <FormControlLabel
            control={
              <Checkbox
                name='isActive'
                color='primary'
                onClick={() => setFieldValue('isActive', !values.isActive)}
                size='small'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.isActive || true}
                checked={values.isActive}
              />
            }
            label='Active'
          />
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ExhibitInfo;
